import React from "react";

type Props = {
  /** 0-based index of the CURRENT step */
  step: number;
  /** Optional custom labels; default aligns to your pipeline */
  labels?: string[];
  /** "sm" | "md" | "lg" height presets */
  size?: "sm" | "md" | "lg";
  className?: string;
};

const DEFAULT_LABELS = ["oEmbed", "Probe", "Frames", "OCR", "Search"];

export function StepBar({
  step,
  labels = DEFAULT_LABELS,
  size = "md",
  className = "",
}: Props) {
  const n = Math.max(1, labels.length);
  // Clamp to a valid index
  const current = Math.min(Math.max(0, Math.floor(step ?? 0)), n - 1);

  const h =
    size === "sm" ? "h-1" :
    size === "lg" ? "h-2" :
    "h-1.5"; // md

  const barBase = `${h} rounded transition-all duration-150`;
  const status = (i: number) =>
    i < current ? "complete" : i === current ? "current" : "upcoming";

  // Overall progress info for assistive tech
  const ariaText = `${labels[current]} (${current + 1} of ${n})`;

  return (
    <div
      className={["w-full", className].filter(Boolean).join(" ")}
      aria-live="polite"
      aria-label="Analysis progress"
    >
      {/* SR-only progressbar so screen readers get a concise announcement */}
      <div
        className="sr-only"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={n}
        aria-valuenow={current + 1}
        aria-valuetext={ariaText}
      />
      <ol 
        className={`grid gap-2 ${
          labels.length <= 3 ? 'grid-cols-3' :
          labels.length <= 4 ? 'grid-cols-4' : 
          labels.length <= 5 ? 'grid-cols-5' :
          'grid-cols-6'
        }`} 
        role="list"
      >
        {labels.map((label, i) => {
          const st = status(i);
          const cls =
            st === "complete"
              ? "bg-emerald-400"
              : st === "current"
              ? "bg-emerald-300"
              : "bg-white/10";
          return (
            <li
              key={label}
              role="listitem"
              title={label}
              aria-current={i === current ? "step" : undefined}
              data-state={st} // handy for tests/theming
              className={[barBase, cls].join(" ")}
            >
              <span className="sr-only">
                {label} — {st}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
